import type { StrictPaddingStyle } from "../util/types";

import { normalizeTextStyle } from "../Text/normalizeTextStyle";
import { type TextLevel, getFontSizeStyle } from "../Text/TextLevel";
import { useTheme } from "../ThemeProvider";

import React, { type ReactNode, useCallback, useState } from "react";
import { Linking, Platform, Pressable, StyleSheet, Text, type TextStyle, View } from "react-native";

/**
 * Normalizes a URL so that `Linking.canOpenURL` / `Linking.openURL` can
 * resolve it. User-entered URLs may lack a scheme (e.g. "indeed.com") or
 * use mixed-case schemes (e.g. "Https://"). This ensures a well-formed
 * URL that the device browser can open.
 */
export function normalizeUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) {
    return url.replace(/^https?:\/\//i, (match) => match.toLowerCase());
  }
  return `https://${url}`;
}

export type TextLinkVariant = "standard" | "neutral" | "inverse" | "strong";

export interface TextLinkProps {
  /**
   * The URL to navigate to when the link is pressed
   */
  targetUrl?: string;

  /**
   * [Web] Specifies where to open the linked document.
   * @default '_self'
   */
  target?: "_blank" | "_self" | "_parent" | "_top";

  /**
   * [Web] Specifies the relationship between the current document and the linked document.
   * Recommended when using target="_blank".
   */
  rel?: string;

  /**
   * Styles the TextLink appropriately
   */
  variant?: TextLinkVariant;

  /**
   * Text level from 1 (smallest) to 8 (largest)
   */
  level?: TextLevel;

  /**
   * The text content of the link to render
   */
  children: ReactNode;

  /**
   * function to render an icon to appear after (to the right of, not considering RTL at the moment) the link text.
   */
  iconAfter?: ReactNode;

  /**
   * Additional callback for "press" action
   */
  onPress?: () => void;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle & Pick<TextStyle, "textDecorationLine">;

  /**
   * Accessibility label for screen readers
   */
  "aria-label"?: string;

  /**
   * When true and on Android, uses a Pressable wrapper instead of Text
   * to avoid issues with TextLink nested inside a Text component.
   */
  inline?: boolean;

  /**
   * Anchor element attributes for the web platform, mirroring react-native-web's API.
   * Ignored on native platforms.
   */
  webHrefAttrs?: { target?: string; rel?: string; download?: string };
}

const TEXT_LEVEL_TO_ICON_SIZE: Record<TextLevel, number> = {
  1: 13,
  2: 14.5,
  3: 16,
  4: 21,
  5: 24,
  6: 29,
  7: 37,
  8: 45,
};

export const TextLink = function ({
  children,
  targetUrl,
  target = "_self",
  rel,
  variant = "standard",
  level = 3,
  iconAfter,
  onPress,
  sx,
  "aria-label": ariaLabel,
  inline,
  webHrefAttrs,
}: TextLinkProps) {
  const theme = useTheme();
  // Add state to track if the link is being pressed
  const [isPressed, setIsPressed] = useState(false);

  const COLORS = {
    standard: {
      default: theme.colors.foregroundActionPrimary,
      active: theme.colors.foregroundActionPrimaryActive,
    },
    neutral: {
      default: theme.colors.foregroundAction,
      active: theme.colors.foregroundActionActive,
    },
    strong: {
      default: theme.colors.foregroundActionStrong,
      active: theme.colors.foregroundActionStrongActive,
    },
    inverse: {
      default: theme.colors.foregroundActionInverse,
      active: theme.colors.foregroundActionInverseActive,
    },
  };

  const handlePress = useCallback(async () => {
    /**
     * On Web, if a targetUrl is provided, the component renders as an <a> tag.
     * We let the browser handle the navigation natively via 'href' and 'hrefAttrs'
     * to avoid double-triggering the link and to support native features
     * like "Open in new tab".
     */
    if (Platform.OS === "web" && targetUrl) {
      return;
    }
    if (targetUrl !== undefined) {
      const url = normalizeUrl(targetUrl);
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      }
    } else if (onPress) {
      onPress();
    }
  }, [onPress, targetUrl]);

  const textColor = isPressed ? COLORS[variant].active : COLORS[variant].default;

  const fontStyle = getFontSizeStyle(level, theme);

  const textColorStyle: TextStyle = {
    color: textColor,
  };

  const styles = StyleSheet.create({
    icon: {
      marginLeft: 2,
    },
    text: {
      textDecorationLine: "underline",
      alignSelf: "auto",
      textAlign: "left", // required for right-to-left support
      fontFamily: theme.fonts.default,
      // `textAlignVertical: 'center'` keeps inline links centered on Android.
      // On web, react-native-web maps it to `vertical-align: middle`, which
      // drops an inline link below the surrounding text's baseline (RN-4440),
      // so omit it there and let the link sit on the default baseline.
      ...Platform.select({
        web: {},
        default: { textAlignVertical: "center" as const },
      }),
    },
  });

  const iconSize = TEXT_LEVEL_TO_ICON_SIZE[level];
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const lineHeight = fontStyle.lineHeight as number;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const fontSize = fontStyle.fontSize as number;
  const verticalPadding = (lineHeight - fontSize) / 2;

  const pressableStyle = {
    minHeight: lineHeight,
    transform: [{ translateY: verticalPadding }],
  };

  const styledIcon = iconAfter
    ? React.cloneElement(
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        iconAfter as React.ReactElement<{
          color?: string;
          width?: number;
          height?: number;
          transform?: Array<{ translateY: number }>;
          style?: any;
          "aria-hidden"?: boolean;
        }>,
        {
          color: textColor,
          width: iconSize,
          height: iconSize,
          // This keeps the icon aligned with the bottom of the text
          transform: [{ translateY: iconSize / 6 }],
          style: styles.icon,
          "aria-hidden": true,
        },
      )
    : null;

  // On Android, nesting a <Text onPress> inside another <Text> causes touch issues,
  // so inline TextLinks use a Pressable wrapper. On iOS, a standalone <Text onPress>
  // is not focusable via Full Keyboard Access (FKA), so non-inline TextLinks use a
  // Pressable wrapper to register as a focusable control.
  const usesPressableWrapper =
    (Platform.OS === "android" && inline) || (Platform.OS === "ios" && !inline);
  const normalizedTextStyle = normalizeTextStyle(
    StyleSheet.flatten([textColorStyle, fontStyle, styles.text, sx]),
  );

  if (usesPressableWrapper) {
    return (
      <Pressable
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- FIXME
        onPress={handlePress}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        accessibilityRole="link"
        role="link"
        aria-label={ariaLabel}
        accessible={true}
        style={Platform.OS === "android" ? pressableStyle : undefined}
      >
        <Text style={normalizedTextStyle}>
          {children}
          {Platform.OS === "android" ? (
            <View>{styledIcon && styledIcon}</View>
          ) : (
            styledIcon && styledIcon
          )}
        </Text>
      </Pressable>
    );
  }

  return (
    <Text
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- FIXME
      onPress={handlePress}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      {...Platform.select({
        web: {
          href: targetUrl,
          hrefAttrs: {
            target: target,
            rel: target === "_blank" ? (rel ?? "noopener noreferrer") : rel,
          },
        },
      })}
      accessibilityRole="link"
      aria-label={ariaLabel}
      accessible={true}
      style={normalizedTextStyle}
      // @ts-expect-error href and hrefAttrs are valid react-native-web props not in RN type definitions
      href={Platform.OS === "web" ? targetUrl : undefined}
      hrefAttrs={Platform.OS === "web" ? webHrefAttrs : undefined}
    >
      {children}
      {/** Decorative icon */}
      {styledIcon && styledIcon}
    </Text>
  );
};
